import { UserRoleName } from '@brands/services/identity/types/UserProfile';

export type AddressMode = 'full' | 'zip' | 'none';

/**
 * Drives which fields the first-launch "Complete your profile" form renders
 * for each non-patient role, so it mirrors that role's own account-menu
 * my-profile page. Patient keeps the original dedicated form in
 * `CompleteProfile.tsx` and does not use this config.
 */
export interface RoleFormConfig {
  submitLabel: string;
  /** Collector only: render every field read-only and just confirm/continue. */
  readOnly: boolean;
  /** Provider: name is managed by admin-created user data and is not editable by the provider. */
  lockNameFields: boolean;
  showMiddleName: boolean;
  /** Email is not part of provider my-profile edit, but remains visible for other roles. */
  showEmailDisplay: boolean;
  /** gender assigned at birth + gender identity + pronouns. */
  showGenderBlock: boolean;
  showLanguages: boolean;
  addressMode: AddressMode;
  /** Provider: education, specialties, additional credentials, biography. */
  showProviderFields: boolean;
  /** Standalone biography (CareAdvisor). Provider's biography is part of provider fields. */
  showBiography: boolean;
  /** Read-only organization display (Collector / OrganizationAdmin). */
  showOrganization: boolean;
}

const DEFAULT_CONFIG: RoleFormConfig = {
  submitLabel: 'Save Profile',
  readOnly: false,
  lockNameFields: false,
  showMiddleName: true,
  showEmailDisplay: true,
  showGenderBlock: false,
  showLanguages: false,
  addressMode: 'none',
  showProviderFields: false,
  showBiography: false,
  showOrganization: false,
};

const ROLE_CONFIG: Partial<Record<UserRoleName, RoleFormConfig>> = {
  [UserRoleName.Provider]: {
    ...DEFAULT_CONFIG,
    lockNameFields: true,
    showEmailDisplay: false,
    showMiddleName: true,
    showGenderBlock: true,
    showLanguages: true,
    addressMode: 'full',
    showProviderFields: true,
  },
  [UserRoleName.CareAdvisor]: {
    ...DEFAULT_CONFIG,
    showMiddleName: true,
    showLanguages: true,
    addressMode: 'zip',
    showBiography: true,
  },
  [UserRoleName.Collector]: {
    ...DEFAULT_CONFIG,
    submitLabel: 'Confirm and continue',
    readOnly: true,
    showMiddleName: true,
    addressMode: 'none',
    showOrganization: true,
  },
  [UserRoleName.Admin]: {
    ...DEFAULT_CONFIG,
    showMiddleName: false,
    addressMode: 'full',
  },
  [UserRoleName.SuperAdmin]: {
    ...DEFAULT_CONFIG,
    showMiddleName: true,
    addressMode: 'zip',
  },
  [UserRoleName.OrganizationAdmin]: {
    ...DEFAULT_CONFIG,
    showMiddleName: false,
    addressMode: 'full',
    showOrganization: true,
  },
};

export const getRoleFormConfig = (role?: string): RoleFormConfig => ROLE_CONFIG[role as UserRoleName] ?? DEFAULT_CONFIG;

/** Roles that should NOT use the generic patient first-launch form. */
export const isRoleSpecificFirstLaunchRole = (role?: string): boolean =>
  !!role && role !== UserRoleName.Patient && role !== UserRoleName.Dependent && role in ROLE_CONFIG;
